import type { MenuOptionType } from "@dexteel/mesf-core";
import EditIcon from "@mui/icons-material/Edit";
import FeedIcon from "@mui/icons-material/Feed";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box, Button, Tooltip } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { ReactNode } from "react";
import { useMemo, useRef } from "react";
import { CheckboxRenderer } from "@/controls/ag-grid/components/CheckboxRenderer";
import { PrinterStatusLabel } from "../components/StatusLabel";
import type { Printer } from "../models";
import {
	dpi,
	formatAgo,
	formatDateTime,
	KIND_COLOR,
	shownStatus,
	statusInfo,
	tagSize,
} from "../statusInfo";

type Handlers = {
	onDetails: (p: Printer) => void;
	onEdit: (p: Printer) => void;
	onNew: () => void;
};

export const Cell = ({ children }: { children: ReactNode }) => (
	<Box
		sx={{
			height: "100%",
			display: "flex",
			alignItems: "center",
			gap: 0.75,
			lineHeight: 1.5,
		}}
	>
		{children}
	</Box>
);

const actionBtnSx = { minWidth: 32, padding: "4px 8px" } as const;
const centered = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	padding: 0,
} as const;
type Params = ICellRendererParams<Printer>;

export const useTableData = (handlers: Handlers) => {
	const h = useRef(handlers);
	h.current = handlers;

	const columnDefs = useMemo(() => {
		const cols: ColDef<Printer>[] = [
			{
				field: "PrinterName",
				headerName: "Printer",
				flex: 1.3,
				minWidth: 140,
			},
			{ field: "AssetName", headerName: "Station", flex: 1, minWidth: 95 },
			{
				colId: "Status",
				headerName: "Status",
				flex: 1,
				minWidth: 110,
				valueGetter: ({ data }) =>
					data ? statusInfo(shownStatus(data)).label : "",
				cellRenderer: ({ data }: Params) =>
					data && (
						<Cell>
							<PrinterStatusLabel status={shownStatus(data)} />
						</Cell>
					),
			},
			{
				field: "LastContactAt",
				headerName: "Last contact",
				flex: 1.5,
				minWidth: 240,
				cellRenderer: ({ value }: Params) => {
					const ago = formatAgo(value);
					return (
						<span>
							{formatDateTime(value)}
							{ago && (
								<span style={{ fontSize: "0.7rem", color: KIND_COLOR.warn }}>
									{" "}
									· {ago}
								</span>
							)}
						</span>
					);
				},
			},
			{
				field: "LastPrintedAt",
				headerName: "Last printed",
				flex: 1,
				minWidth: 164,
				valueFormatter: ({ value }) => formatDateTime(value),
			},
			{
				field: "LabelCount",
				headerName: "Tags printed",
				flex: 0.8,
				minWidth: 100,
				valueFormatter: ({ value }) =>
					value == null ? "—" : value.toLocaleString(),
			},
			{
				field: "Model",
				headerName: "Model",
				flex: 1,
				minWidth: 130,
				cellRenderer: ({ data }: Params) => {
					if (!data?.Model) return "—";
					const more = [
						data.SerialNumber && `Serial ${data.SerialNumber}`,
						data.Firmware && `Firmware ${data.Firmware}`,
					]
						.filter(Boolean)
						.join(" · ");
					return (
						<Cell>
							{data.Model}
							{more && (
								<Tooltip title={more} arrow>
									<InfoOutlinedIcon
										sx={{ fontSize: 16, color: "action.active", ml: -0.25 }}
									/>
								</Tooltip>
							)}
						</Cell>
					);
				},
			},
			{
				field: "MacAddress",
				headerName: "MAC",
				flex: 1.1,
				minWidth: 145,
				valueFormatter: ({ value }) => value ?? "—",
			},
			{ field: "IpAddress", headerName: "IP", flex: 1, minWidth: 130 },
			{ field: "Port", headerName: "Port", flex: 0.5, minWidth: 56 },
			{
				colId: "Tag",
				headerName: "Tag",
				flex: 1,
				minWidth: 104,
				valueGetter: ({ data }) => (data ? tagSize(data) : ""),
				tooltipValueGetter: ({ data }) => (data && dpi(data.Dpmm)) || null,
			},
			{
				field: "IsActive",
				headerName: "Active",
				minWidth: 64,
				maxWidth: 64,
				cellStyle: centered,
				cellRenderer: CheckboxRenderer,
			},
			{
				colId: "actions",
				headerName: "",
				minWidth: 52,
				maxWidth: 52,
				sortable: false,
				cellStyle: centered,
				cellRenderer: ({ data }: Params) =>
					data && (
						<Button
							title="Edit"
							size="small"
							sx={actionBtnSx}
							onClick={() => h.current.onEdit(data)}
						>
							<EditIcon sx={{ fontSize: 18 }} color="primary" />
						</Button>
					),
			},
		];
		// app grids center; this one left
		return cols.map((c) => ({
			headerClass: "left-aligned-header",
			cellStyle: { textAlign: "left" },
			...c,
		}));
	}, []);

	const getMenuOptions = (data: Printer | undefined): MenuOptionType[] => [
		...(data
			? [
					{
						name: "Printer Details",
						key: "printer_details",
						onClick: () => h.current.onDetails(data),
						icon: <FeedIcon />,
						style: { fontWeight: "bold" },
					},
					{
						name: "Edit Printer",
						key: "edit_printer",
						onClick: () => h.current.onEdit(data),
						icon: <EditIcon />,
					},
				]
			: []),
		{
			name: "New Printer",
			key: "new_printer",
			onClick: () => h.current.onNew(),
			icon: <PlaylistAddIcon />,
		},
	];

	return { columnDefs, getMenuOptions };
};
